import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { AdminLayout } from "@/pages/AdminDashboard";
import api from "@/services/api";
import { toast } from "sonner";
import {
  Calendar,
  MapPin,
  Palette,
  Briefcase,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Tags
} from "lucide-react";

const TABS = [
  { id: 'events', label: 'Nuptial Events', icon: Calendar },
  { id: 'cities', label: 'Cities', icon: MapPin },
  { id: 'themes', label: 'Themes', icon: Palette },
  { id: 'services', label: 'Services & Tags', icon: Briefcase }
];

export default function AdminFormOptions() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('events');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    events: [],
    cities: [],
    themes: [],
    services: []
  });

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [eventsRes, citiesRes, themesRes, servicesRes] = await Promise.all([
        api.getNuptialEvents(token),
        api.getCities(token),
        api.getThemes(token),
        api.getMainServices(token)
      ]);

      setData({
        events: eventsRes.events || [],
        cities: citiesRes.cities || [],
        themes: themesRes.themes || [],
        services: servicesRes.services || []
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load form options");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <AdminLayout>
      <div className="p-6 md:p-8" data-testid="admin-form-options">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-light text-white">Form Options</h1>
          <p className="text-gray-400 mt-1">Manage dynamic options for the wedding intake form</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-[#c4646a] text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c4646a]"></div>
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-xl">
            {activeTab === 'events' && (
              <EventsManager data={data.events} token={token} onRefresh={fetchData} />
            )}
            {activeTab === 'cities' && (
              <CitiesManager data={data.cities} token={token} onRefresh={fetchData} />
            )}
            {activeTab === 'themes' && (
              <ThemesManager data={data.themes} token={token} onRefresh={fetchData} />
            )}
            {activeTab === 'services' && (
              <ServicesManager data={data.services} token={token} onRefresh={fetchData} />
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// ============== EVENTS MANAGER ==============
function EventsManager({ data, token, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newName, setNewName] = useState('');
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const response = await api.addNuptialEvent({ name: newName, active: true, order: data.length }, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('Event added successfully');
        setNewName('');
        setShowAdd(false);
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to add event');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      const response = await api.updateNuptialEvent({ id, name: editName }, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('Event updated successfully');
        setEditingId(null);
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      const response = await api.deleteNuptialEvent(id, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('Event deleted successfully');
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await api.updateNuptialEvent({ id: item.id, active: !item.active }, token);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-white">Nuptial Events</h3>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-2 px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Event</span>
        </button>
      </div>

      {showAdd && (
        <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="flex gap-3">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter event name..."
              className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#c4646a]"
            />
            <button onClick={handleAdd} disabled={saving || !newName.trim()} className="px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setShowAdd(false); setNewName(''); }} className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {data.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No events added yet</p>
        ) : (
          data.map((item) => (
            <div key={item.id} className={`flex items-center justify-between p-4 rounded-lg border ${item.active !== false ? 'bg-white/5 border-white/10' : 'bg-white/2 border-white/5 opacity-60'}`}>
              {editingId === item.id ? (
                <div className="flex-1 flex gap-3">
                  <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#c4646a]" autoFocus />
                  <button onClick={() => handleUpdate(item.id)} disabled={saving} className="p-2 text-green-400 hover:bg-green-400/10 rounded-lg"><Check className="w-5 h-5" /></button>
                  <button onClick={() => setEditingId(null)} className="p-2 text-gray-400 hover:bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-gray-500" />
                    <span className="text-white">{item.name}</span>
                    {item.active === false && <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">Inactive</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleToggleActive(item)} className={`p-2 rounded-lg ${item.active !== false ? 'text-green-400 hover:bg-green-400/10' : 'text-gray-400 hover:bg-white/10'}`} title={item.active !== false ? 'Deactivate' : 'Activate'}><Check className="w-4 h-4" /></button>
                    <button onClick={() => { setEditingId(item.id); setEditName(item.name); }} className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(item.id)} className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============== CITIES MANAGER ==============
function CitiesManager({ data, token, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newItem, setNewItem] = useState({ name: '', category: 'National' });
  const [editItem, setEditItem] = useState({ name: '', category: '' });
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(['Preference', 'National', 'International']);

  const categories = ['Preference', 'National', 'International'];
  const groupedData = categories.reduce((acc, cat) => {
    acc[cat] = data.filter(d => d.category === cat);
    return acc;
  }, {});

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const handleAdd = async () => {
    if (!newItem.name.trim()) return;
    setSaving(true);
    try {
      const response = await api.addCity({ ...newItem, active: true, order: data.length }, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('City added successfully');
        setNewItem({ name: '', category: 'National' });
        setShowAdd(false);
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to add city');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editItem.name.trim()) return;
    setSaving(true);
    try {
      const response = await api.updateCity({ id, ...editItem }, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('City updated successfully');
        setEditingId(null);
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to update city');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this city?')) return;
    try {
      const response = await api.deleteCity(id, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('City deleted successfully');
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to delete city');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-white">Wedding Cities/Destinations</h3>
        <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors">
          <Plus className="w-4 h-4" />
          <span>Add City</span>
        </button>
      </div>

      {showAdd && (
        <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="grid md:grid-cols-3 gap-3">
            <input type="text" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} placeholder="City name..." className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#c4646a]" />
            <select value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })} className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#c4646a]">
              {categories.map(cat => <option key={cat} value={cat} className="bg-[#1a1a1a]">{cat}</option>)}
            </select>
            <div className="flex gap-2">
              <button onClick={handleAdd} disabled={saving || !newItem.name.trim()} className="flex-1 px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
              <button onClick={() => { setShowAdd(false); setNewItem({ name: '', category: 'National' }); }} className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {categories.map((category) => (
          <div key={category} className="border border-white/10 rounded-lg overflow-hidden">
            <button onClick={() => toggleCategory(category)} className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors">
              <span className="text-white font-medium">{category} ({groupedData[category]?.length || 0})</span>
              {expandedCategories.includes(category) ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>
            {expandedCategories.includes(category) && (
              <div className="p-4 space-y-2">
                {groupedData[category]?.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">No cities in this category</p>
                ) : (
                  groupedData[category]?.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      {editingId === item.id ? (
                        <div className="flex-1 flex gap-3">
                          <input type="text" value={editItem.name} onChange={(e) => setEditItem({ ...editItem, name: e.target.value })} className="flex-1 px-3 py-1 bg-white/5 border border-white/10 rounded text-white text-sm" autoFocus />
                          <select value={editItem.category} onChange={(e) => setEditItem({ ...editItem, category: e.target.value })} className="px-3 py-1 bg-white/5 border border-white/10 rounded text-white text-sm">
                            {categories.map(cat => <option key={cat} value={cat} className="bg-[#1a1a1a]">{cat}</option>)}
                          </select>
                          <button onClick={() => handleUpdate(item.id)} className="p-1 text-green-400"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditingId(null)} className="p-1 text-gray-400"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <>
                          <span className="text-white">{item.name}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => { setEditingId(item.id); setEditItem({ name: item.name, category: item.category }); }} className="p-1 text-blue-400 hover:bg-blue-400/10 rounded"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => handleDelete(item.id)} className="p-1 text-red-400 hover:bg-red-400/10 rounded"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== THEMES MANAGER ==============
function ThemesManager({ data, token, onRefresh }) {
  const [showAddTheme, setShowAddTheme] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingThemeId, setEditingThemeId] = useState(null);
  const [editingCatId, setEditingCatId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newTheme, setNewTheme] = useState({ name: '', category: '', description: '' });
  const [editTheme, setEditTheme] = useState({ name: '', category: '', description: '' });
  const [newCatName, setNewCatName] = useState('');
  const [editCatName, setEditCatName] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]);

  const fetchCategories = async () => {
    try {
      const res = await api.getThemeCategories(token);
      const cats = res.categories || [];
      setCategories(cats);
      if (cats.length > 0 && !newTheme.category) {
        setNewTheme(prev => ({ ...prev, category: cats[0].name }));
      }
    } catch {}
  };

  useEffect(() => { fetchCategories(); }, []); // eslint-disable-line

  const categoryNames = categories.map(c => c.name);
  const groupedData = categoryNames.reduce((acc, cat) => {
    acc[cat] = data.filter(d => d.category === cat);
    return acc;
  }, {});
  // Also show themes with categories not in the list
  const uncatThemes = data.filter(d => !categoryNames.includes(d.category));
  if (uncatThemes.length > 0) {
    groupedData['Uncategorized'] = uncatThemes;
  }
  const allCategoryNames = uncatThemes.length > 0 ? [...categoryNames, 'Uncategorized'] : categoryNames;

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const handleAddTheme = async () => {
    if (!newTheme.name.trim() || !newTheme.category) return;
    setSaving(true);
    try {
      const response = await api.addTheme({ ...newTheme, active: true, order: data.length }, token);
      if (response.error) { toast.error(response.error); }
      else { toast.success('Theme added'); setNewTheme({ name: '', category: categories[0]?.name || '', description: '' }); setShowAddTheme(false); onRefresh(); }
    } catch { toast.error('Failed to add theme'); }
    finally { setSaving(false); }
  };

  const handleUpdateTheme = async (id) => {
    if (!editTheme.name.trim()) return;
    setSaving(true);
    try {
      const response = await api.updateTheme({ id, ...editTheme }, token);
      if (response.error) { toast.error(response.error); }
      else { toast.success('Theme updated'); setEditingThemeId(null); onRefresh(); }
    } catch { toast.error('Failed to update theme'); }
    finally { setSaving(false); }
  };

  const handleDeleteTheme = async (id) => {
    if (!window.confirm('Delete this theme?')) return;
    try {
      const response = await api.deleteTheme(id, token);
      if (response.error) { toast.error(response.error); }
      else { toast.success('Theme deleted'); onRefresh(); }
    } catch { toast.error('Failed to delete theme'); }
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    setSaving(true);
    try {
      const response = await api.addThemeCategory({ name: newCatName, order: categories.length }, token);
      if (response.error) { toast.error(response.error); }
      else { toast.success('Category added'); setNewCatName(''); setShowAddCategory(false); fetchCategories(); onRefresh(); }
    } catch { toast.error('Failed to add category'); }
    finally { setSaving(false); }
  };

  const handleUpdateCategory = async (id) => {
    if (!editCatName.trim()) return;
    setSaving(true);
    try {
      const response = await api.updateThemeCategory({ id, name: editCatName }, token);
      if (response.error) { toast.error(response.error); }
      else { toast.success('Category updated'); setEditingCatId(null); fetchCategories(); onRefresh(); }
    } catch { toast.error('Failed to update category'); }
    finally { setSaving(false); }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category? Themes in it will move to Uncategorized.')) return;
    try {
      const response = await api.deleteThemeCategory(id, token);
      if (response.error) { toast.error(response.error); }
      else { toast.success('Category deleted'); fetchCategories(); onRefresh(); }
    } catch { toast.error('Failed to delete category'); }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-white">Wedding Vision Themes</h3>
        <div className="flex gap-2">
          <button onClick={() => setShowAddCategory(!showAddCategory)} className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm">
            <Plus className="w-4 h-4" />
            <span>Add Category</span>
          </button>
          <button onClick={() => setShowAddTheme(!showAddTheme)} className="flex items-center gap-2 px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors">
            <Plus className="w-4 h-4" />
            <span>Add Theme</span>
          </button>
        </div>
      </div>

      {/* Add Category Form */}
      {showAddCategory && (
        <div className="mb-4 p-4 bg-white/5 rounded-lg border border-white/10">
          <p className="text-gray-400 text-sm mb-2">New Category Name</p>
          <div className="flex gap-3">
            <input type="text" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="e.g. Boho & Rustic" className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#c4646a]" onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()} />
            <button onClick={handleAddCategory} disabled={saving || !newCatName.trim()} className="px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
            <button onClick={() => { setShowAddCategory(false); setNewCatName(''); }} className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20">Cancel</button>
          </div>
        </div>
      )}

      {/* Add Theme Form */}
      {showAddTheme && (
        <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <input type="text" value={newTheme.name} onChange={(e) => setNewTheme({ ...newTheme, name: e.target.value })} placeholder="Theme name..." className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#c4646a]" />
              <select value={newTheme.category} onChange={(e) => setNewTheme({ ...newTheme, category: e.target.value })} className="px-4 py-2 bg-[#1a1a1a] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#c4646a]">
                {categoryNames.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <textarea value={newTheme.description} onChange={(e) => setNewTheme({ ...newTheme, description: e.target.value })} placeholder="Theme description (optional)..." className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#c4646a] min-h-[80px]" />
            <div className="flex gap-2">
              <button onClick={handleAddTheme} disabled={saving || !newTheme.name.trim()} className="px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
              <button onClick={() => { setShowAddTheme(false); setNewTheme({ name: '', category: categories[0]?.name || '', description: '' }); }} className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="space-y-4">
        {/* Category management row */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {categories.map(cat => (
              <div key={cat.id} className="flex items-center gap-1 px-3 py-1 bg-white/5 border border-white/10 rounded-full text-sm">
                {editingCatId === cat.id ? (
                  <>
                    <input type="text" value={editCatName} onChange={(e) => setEditCatName(e.target.value)} className="bg-transparent text-white border-b border-white/30 focus:outline-none w-24 text-sm" autoFocus onKeyDown={(e) => e.key === 'Enter' && handleUpdateCategory(cat.id)} />
                    <button onClick={() => handleUpdateCategory(cat.id)} className="text-green-400 hover:text-green-300"><Check className="w-3 h-3" /></button>
                    <button onClick={() => setEditingCatId(null)} className="text-gray-400 hover:text-white"><X className="w-3 h-3" /></button>
                  </>
                ) : (
                  <>
                    <span className="text-gray-300">{cat.name}</span>
                    <button onClick={() => { setEditingCatId(cat.id); setEditCatName(cat.name); }} className="text-blue-400 hover:text-blue-300 ml-1"><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {allCategoryNames.map((category) => (
          <div key={category} className="border border-white/10 rounded-lg overflow-hidden">
            <button onClick={() => toggleCategory(category)} className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors">
              <span className="text-white font-medium">{category} ({groupedData[category]?.length || 0})</span>
              {expandedCategories.includes(category) ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>
            {expandedCategories.includes(category) && (
              <div className="p-4 space-y-2">
                {groupedData[category]?.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">No themes in this category</p>
                ) : (
                  groupedData[category]?.map((item) => (
                    <div key={item.id} className="p-3 bg-white/5 rounded-lg">
                      {editingThemeId === item.id ? (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <input type="text" value={editTheme.name} onChange={(e) => setEditTheme({ ...editTheme, name: e.target.value })} className="flex-1 px-3 py-1 bg-white/5 border border-white/10 rounded text-white text-sm" autoFocus />
                            <select value={editTheme.category} onChange={(e) => setEditTheme({ ...editTheme, category: e.target.value })} className="px-3 py-1 bg-[#1a1a1a] border border-white/10 rounded text-white text-sm">
                              {categoryNames.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                          </div>
                          <textarea value={editTheme.description} onChange={(e) => setEditTheme({ ...editTheme, description: e.target.value })} className="w-full px-3 py-1 bg-white/5 border border-white/10 rounded text-white text-sm min-h-[60px]" placeholder="Description..." />
                          <div className="flex gap-2">
                            <button onClick={() => handleUpdateTheme(item.id)} className="p-1 text-green-400"><Check className="w-4 h-4" /></button>
                            <button onClick={() => setEditingThemeId(null)} className="p-1 text-gray-400"><X className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div>
                            <span className="text-white font-medium">{item.name}</span>
                            {item.description && <p className="text-gray-400 text-sm mt-1">{item.description}</p>}
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => { setEditingThemeId(item.id); setEditTheme({ name: item.name, category: item.category, description: item.description || '' }); }} className="p-1 text-blue-400 hover:bg-blue-400/10 rounded"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => handleDeleteTheme(item.id)} className="p-1 text-red-400 hover:bg-red-400/10 rounded"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== SERVICES MANAGER (with Tags) ==============
function ServicesManager({ data, token, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newName, setNewName] = useState('');
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedServices, setExpandedServices] = useState([]);
  const [newTag, setNewTag] = useState({});

  const toggleService = (id) => {
    setExpandedServices(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const handleAddService = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const response = await api.addMainService({ name: newName, active: true, order: data.length }, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('Service added successfully');
        setNewName('');
        setShowAdd(false);
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to add service');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateService = async (id) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      const response = await api.updateMainService({ id, name: editName }, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('Service updated successfully');
        setEditingId(null);
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to update service');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Delete this service and all its tags?')) return;
    try {
      const response = await api.deleteMainService(id, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('Service deleted successfully');
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleAddTag = async (serviceId) => {
    const raw = newTag[serviceId] || '';
    // Support comma-separated bulk entry
    const names = raw.split(',').map(s => s.trim()).filter(Boolean);
    if (names.length === 0) return;
    try {
      for (const name of names) {
        const response = await api.addServiceTag({ service_id: serviceId, name, active: true }, token);
        if (response.error) { toast.error(response.error); return; }
      }
      toast.success(names.length > 1 ? `${names.length} tags added` : 'Tag added');
      setNewTag({ ...newTag, [serviceId]: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to add tag');
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      const response = await api.deleteServiceTag(tagId, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('Tag deleted');
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to delete tag');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-white">Main Services & Tags</h3>
          <p className="text-gray-400 text-sm mt-1">Manage services and their sub-tags for the form</p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors">
          <Plus className="w-4 h-4" />
          <span>Add Service</span>
        </button>
      </div>

      {showAdd && (
        <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="flex gap-3">
            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Enter service name..." className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#c4646a]" />
            <button onClick={handleAddService} disabled={saving || !newName.trim()} className="px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
            <button onClick={() => { setShowAdd(false); setNewName(''); }} className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {data.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No services added yet</p>
        ) : (
          data.map((service) => (
            <div key={service.id} className="border border-white/10 rounded-lg overflow-hidden">
              {/* Service Header */}
              <div className="flex items-center justify-between p-4 bg-white/5">
                {editingId === service.id ? (
                  <div className="flex-1 flex gap-3">
                    <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#c4646a]" autoFocus />
                    <button onClick={() => handleUpdateService(service.id)} disabled={saving} className="p-2 text-green-400 hover:bg-green-400/10 rounded-lg"><Check className="w-5 h-5" /></button>
                    <button onClick={() => setEditingId(null)} className="p-2 text-gray-400 hover:bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
                  </div>
                ) : (
                  <>
                    <button onClick={() => toggleService(service.id)} className="flex items-center gap-3 text-left flex-1">
                      {expandedServices.includes(service.id) ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      <span className="text-white font-medium">{service.name}</span>
                      <span className="text-gray-400 text-sm">({service.tags?.length || 0} tags)</span>
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setEditingId(service.id); setEditName(service.name); }} className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteService(service.id)} className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </>
                )}
              </div>

              {/* Service Tags */}
              {expandedServices.includes(service.id) && (
                <div className="p-4 border-t border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <Tags className="w-4 h-4 text-[#c4646a]" />
                    <span className="text-sm text-gray-400">Tags for {service.name}</span>
                  </div>

                  {/* Add Tag Input — above tag list so new tags appear below */}
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={newTag[service.id] || ''}
                      onChange={(e) => setNewTag({ ...newTag, [service.id]: e.target.value })}
                      placeholder="Add tag(s)... press Enter or use commas: Tag1, Tag2"
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#c4646a]"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); handleAddTag(service.id); }
                      }}
                    />
                    <button
                      onClick={() => handleAddTag(service.id)}
                      disabled={!newTag[service.id]?.trim()}
                      className="px-4 py-2 bg-[#c4646a] text-white rounded-lg text-sm hover:bg-[#a85459] disabled:opacity-50"
                    >
                      Add Tag
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {service.tags?.length === 0 ? (
                      <p className="text-gray-500 text-sm">No tags yet</p>
                    ) : (
                      service.tags?.map((tag) => (
                        <span key={tag.id} className="inline-flex items-center gap-1 px-3 py-1 bg-[#c4646a]/20 text-[#c4646a] rounded-full text-sm">
                          {tag.name}
                          <button onClick={() => handleDeleteTag(tag.id)} className="hover:text-red-400 ml-1"><X className="w-3 h-3" /></button>
                        </span>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
